import { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot, orderBy, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { requestNotificationPermission, onForegroundMessage } from '../firebase/messaging';

export function useNotifications(ustaId) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [fcmToken, setFcmToken] = useState(null);

  // 1. Bildirim izni iste ve FCM token al
  useEffect(() => {
    if (!ustaId) return;

    const initNotifications = async () => {
      const token = await requestNotificationPermission(ustaId);
      setFcmToken(token);
    };

    initNotifications();
  }, [ustaId]);

  // 2. Ön planda gelen bildirimleri dinle
  useEffect(() => {
    const unsubscribe = onForegroundMessage((payload) => {
      // Ön planda bildirim geldiğinde toast/banner göster
      if (payload.notification) {
        showInAppNotification(payload.notification.title, payload.notification.body);
      }
    });

    return () => unsubscribe();
  }, []);

  // 3. Firestore'dan bildirimleri dinle (fallback + okundu takibi)
  useEffect(() => {
    if (!ustaId) return;

    const q = query(
      collection(db, 'bildirimler'),
      where('ustaId', 'in', [ustaId, 'all_ustalar']),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setNotifications(notifs);
      setUnreadCount(notifs.length);

      // Yeni bildirimler için browser notification göster
      notifs.forEach(notif => {
        if (!notif.shown && 'Notification' in window && Notification.permission === 'granted') {
          new Notification(notif.title, {
            body: notif.body,
            icon: '/pwa-192x192.png',
            tag: notif.id,
            data: { url: notif.data?.url || '/' }
          });

          // shown flag'ini güncelle
          updateDoc(doc(db, 'bildirimler', notif.id), { shown: true });
        }
      });
    });

    return () => unsubscribe();
  }, [ustaId]);

  const markAsRead = async (notificationId) => {
    await updateDoc(doc(db, 'bildirimler', notificationId), {
      status: 'read',
      readAt: new Date()
    });
  };

  const markAllAsRead = async () => {
    const promises = notifications.map(n => 
      updateDoc(doc(db, 'bildirimler', n.id), {
        status: 'read',
        readAt: new Date()
      })
    );
    await Promise.all(promises);
  };

  return { notifications, unreadCount, fcmToken, markAsRead, markAllAsRead };
}

// Basit in-app notification gösterici
function showInAppNotification(title, body) {
  // Toast notification oluştur
  const toast = document.createElement('div');
  toast.className = 'fixed top-20 right-4 z-[200] bg-stone-800 border border-stone-700 rounded-2xl p-4 shadow-2xl max-w-sm animate-in slide-in-from-right';
  toast.innerHTML = `
    <div class="flex items-start gap-3">
      <div class="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-emerald-400">
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/>
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>
        </svg>
      </div>
      <div class="flex-1 min-w-0">
        <p class="text-sm font-bold text-white">${title}</p>
        <p class="text-xs text-stone-400 mt-1">${body}</p>
      </div>
      <button onclick="this.parentElement.parentElement.remove()" class="text-stone-500 hover:text-white">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>
    </div>
  `;

  document.body.appendChild(toast);

  // 5 saniye sonra otomatik kaldır
  setTimeout(() => {
    if (toast.parentElement) toast.remove();
  }, 5000);
}